import * as z from 'zod';
import { readCampaignRefSchema, readCampaignSchema } from '../campaigns';
import { brandSchema } from '../brands';
import { readTimeSeriesSchema } from '../common';
import { readImageSchema, updateImageSchema } from '../images';
import { readSubscriptionSchema } from '../subscriptions';
import { readQuotaSchema } from '../quotas';
import { crmIntegrationSchema } from '../crm-integrations';
import { readJobSchema } from '../jobs';

// Enums
// `individual` is legacy (migrated to `business` server-side); `agency` is a
// content-less shell that manages child `business` workspaces.
export const organizationType = z.enum(['individual', 'business', 'agency', 'demo']);
export type OrganizationType = z.infer<typeof organizationType>;
export const organizationStatus = z.enum(['deleted', 'active']);
export const snippetStatusEnum = z.enum(['active', 'not_detected', 'on_error']);
export const analyticsModeEnum = z.enum(['basic', 'advanced']);
export type AnalyticsMode = z.infer<typeof analyticsModeEnum>;

// Nested schemas
export const referralCodeSchema = z.object({
  id: z.uuid(),
  referralCode: z.string(),
});
export type ReferralCode = z.infer<typeof referralCodeSchema>;

// SnippetStatus schemas
// 1. Reference schema (for foreign keys)
export const snippetStatusRefSchema = z.object({
  id: z.uuid(),
});
export type SnippetStatusRef = z.infer<typeof snippetStatusRefSchema>;

// 2. Read schema - all properties nullable (API response)
export const readSnippetStatusSchema = z.object({
  id: z.uuid().nullable(),
  domain: z.string().nullable(),
  status: snippetStatusEnum.nullable(),
  analyticsMode: analyticsModeEnum.nullable(),
  error: z.string().nullable(),
  lastVerificationJob: z.lazy(() => readJobSchema).nullable(),
  lastVerifiedAt: z.iso.datetime().nullable(),
  activatedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
});
export type ReadSnippetStatus = z.infer<typeof readSnippetStatusSchema>;

// 1. Reference schema with label field (for dropdowns, selects, links)
export const readOrganizationRefSchema = z.object({
  id: z.uuid(),
  name: z.string(),
});
export type ReadOrganizationRef = z.infer<typeof readOrganizationRefSchema>;

// 2. Minimal reference schema (for foreign keys)
export const organizationRefSchema = z.object({
  id: z.uuid(),
});
export type OrganizationRef = z.infer<typeof organizationRefSchema>;

// Parent (managing agency) of a child workspace. When the viewer cannot read the
// agency, the API degrades it to `{ id }` only — hence nullable name/type.
export const readOrganizationParentSchema = z.object({
  id: z.uuid(),
  name: z.string().nullable().optional(),
  type: organizationType.nullable().optional(),
});
export type ReadOrganizationParent = z.infer<typeof readOrganizationParentSchema>;

export const ssoWorkspaceStatusEnum = z.enum(['none', 'draft', 'tested', 'enabled', 'enforced']);
export type SsoWorkspaceStatus = z.infer<typeof ssoWorkspaceStatusEnum>;

export const scimWorkspaceStatusEnum = z.enum(['disabled', 'enabled']);
export type ScimWorkspaceStatus = z.infer<typeof scimWorkspaceStatusEnum>;

// 3. Read schema - all properties nullable (API response)
export const readOrganizationSchema = z.object({
  id: z.uuid().nullable(),
  name: z.string().nullable(),
  domain: z.string().nullable(),
  type: organizationType.nullable(),
  parent: readOrganizationParentSchema.nullable(),
  status: organizationStatus.nullable(),
  // Every member must have two-factor authentication. Read-only here: it is switched through
  // `POST` / `DELETE /organizations/{id}/mfa-requirement`, never through PUT/PATCH.
  mfaRequired: z.boolean().nullable(),
  // The workspace may configure enterprise single sign-on. Read-only like `mfaRequired`: it is
  // switched from the back office through `/organizations/{id}/sso-availability`.
  ssoAvailable: z.boolean().nullable(),
  // Where the workspace stands with single sign-on, for every member to see: `none` without a
  // connection, else the connection's lifecycle. The connection itself is admin-only.
  ssoStatus: ssoWorkspaceStatusEnum.nullable(),
  // Whether the workspace's directory manages its members over SCIM, for every member to see: the
  // switch as stored. The SCIM settings themselves (base URL, tokens) are admin-only.
  scimStatus: scimWorkspaceStatusEnum.nullable(),
  createdAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
  deleteAt: z.iso.datetime().nullable(),
  lastReferralCode: referralCodeSchema.nullable(),
  campaigns: z.array(z.lazy(() => readCampaignSchema)).nullable(),
  statistics: z
    .object({
      activeMembersCount: z.number().nullable(),
      publishedCampaignsCount: z.number().nullable(),
      totalCampaignsCount: z.number().nullable(),
      pageVariantsCount: z.number().nullable(),
      publishedPageVariantsCount: z.number().nullable(),
      leadsCount: z.number().nullable(),
      audiencesCount: z.number().nullable(),
      visitsCount: z.number().nullable(),
      visitsVariation: z.number().nullable(),
      visitsTimeSeries: readTimeSeriesSchema.nullable(),
    })
    .nullable(),
  logo: z.object(readImageSchema).nullable(),
  lastBrandVersion: z.lazy(() => brandSchema).nullable(),
  // The organization's latest brand extraction job, running, failed or done
  // (null if none ever ran). A running extraction has no brand version yet:
  // `lastBrandVersion.extractionJob` is the job that produced that version,
  // so only this field says whether an extraction is under way.
  brandExtractionJob: z.lazy(() => readJobSchema).nullable(),
  snippetStatus: readSnippetStatusSchema.nullable(),
  subscription: z.lazy(() => readSubscriptionSchema).nullable(),
  quotas: z.lazy(() => z.array(readQuotaSchema)).nullable(),
  activeQuotas: z.lazy(() => z.array(readQuotaSchema)).nullable(),
  crmIntegrations: z.array(z.lazy(() => crmIntegrationSchema)).nullable(),
});
export type ReadOrganization = z.infer<typeof readOrganizationSchema>;

// 4. Create schema - based on DTO serialization groups
export const createOrganizationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  type: organizationType,
  logo: updateImageSchema.nullable().optional(),
});
export type CreateOrganization = z.infer<typeof createOrganizationSchema>;

// Domain validation regex: matches valid domain names like "example.com", "sub.example.co.uk"
const domainRegex =
  /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

// 4b. Create child schema - `POST /organizations/{agencyId}/organizations`.
// The child is always a `business` workspace: the API ignores any `type`.
export const createChildOrganizationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  domain: z
    .string()
    .regex(domainRegex, 'Please enter a valid domain (e.g., example.com)')
    .or(z.literal(''))
    .nullish(),
});
export type CreateChildOrganization = z.infer<typeof createChildOrganizationSchema>;

// 5. Update schema - based on DTO serialization groups
export const updateOrganizationSchema = z.object({
  name: z.string().min(1, 'Name is required').nullish().optional(),
  domain: z
    .string()
    .min(1, 'Domain is required')
    .regex(domainRegex, 'Please enter a valid domain (e.g., example.com)')
    .nullish()
    .optional(),
});

export type UpdateOrganization = z.infer<typeof updateOrganizationSchema>;

// Snippet analytics mode update request schema
export const updateSnippetAnalyticsModeSchema = z.object({
  analyticsMode: analyticsModeEnum,
});
export type UpdateSnippetAnalyticsMode = z.infer<
  typeof updateSnippetAnalyticsModeSchema
>;

// Snippet verification request schema
export const verifySnippetRequestSchema = z.object({
  domain: z.string().min(1, 'Domain is required'),
  campaigns: z.boolean().optional(),
});
export type VerifySnippetRequest = z.infer<typeof verifySnippetRequestSchema>;

// Campaign verification result schema (per-campaign result within unified verification)
export const readCampaignVerificationResultSchema = z.object({
  campaign: z.lazy(() => readCampaignRefSchema).nullable(),
  url: z.string().nullable(),
  status: snippetStatusEnum.nullable(),
  error: z.string().nullable(),
  verifiedAt: z.iso.datetime().nullable(),
});
export type ReadCampaignVerificationResult = z.infer<
  typeof readCampaignVerificationResultSchema
>;

// Snippet verification result schema (API response from GET snippet-verification-result)
export const snippetVerificationResultSchema = z.object({
  snippetStatusId: z.uuid().nullable(),
  url: z.string().nullable(),
  status: snippetStatusEnum.nullable(),
  error: z.string().nullable(),
  lastVerificationJob: z.lazy(() => readJobSchema).nullable(),
  verifiedAt: z.iso.datetime().nullable(),
  campaigns: z.array(readCampaignVerificationResultSchema).nullable(),
});
export type SnippetVerificationResult = z.infer<
  typeof snippetVerificationResultSchema
>;

// Legacy export for backward compatibility
export const organizationSchema = readOrganizationSchema;
export type Organization = ReadOrganization;
